import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';

export type AppTheme = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeOption {
  value: AppTheme;
  labelKey: string;
  icon: string;
}

@Injectable({ providedIn: 'root' })
export class AppearanceService {
  private readonly STORAGE_KEY = 'app-theme';
  private platformId = inject(PLATFORM_ID);
  private doc = inject(DOCUMENT);

  // Signals for reactive state management
  private currentThemeSignal = signal<AppTheme>('light');
  readonly currentTheme = this.currentThemeSignal.asReadonly();

  // System preference signal (updates when OS preference changes)
  // Default to 'dark' when system preference cannot be detected
  private systemPreferenceSignal = signal<ResolvedTheme>('dark');
  readonly systemPreference = this.systemPreferenceSignal.asReadonly();

  // Computed signal for the actual resolved theme
  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const theme = this.currentThemeSignal();
    if (theme === 'auto') {
      return this.systemPreferenceSignal();
    }
    return theme;
  });

  // Computed signal for dark mode detection
  readonly isDarkMode = computed(() => this.resolvedTheme() === 'dark');

  private mediaQuery: MediaQueryList | null = null;

  constructor() {
    this.setupSystemPreferenceListener();
    this.initializeTheme();
  }

  /**
   * Initialize theme from localStorage or detect from system preference
   * Priority:
   * 1. User's manual choice (stored in localStorage)
   * 2. System preference (prefers-color-scheme)
   * 3. Default to 'dark' if system preference cannot be detected
   */
  private initializeTheme(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const storedTheme = localStorage.getItem(this.STORAGE_KEY) as AppTheme | null;

    if (storedTheme && ['light', 'dark', 'auto'].includes(storedTheme)) {
      // User has manually chosen a theme - use it
      this.currentThemeSignal.set(storedTheme);
      console.log('[AppearanceService] Using stored theme from localStorage:', storedTheme);
    } else {
      // No manual choice - use 'auto' to follow system preference
      // If system preference cannot be detected, systemPreferenceSignal defaults to 'dark'
      this.currentThemeSignal.set('auto');
      console.log('[AppearanceService] No stored preference - using auto mode (system preference:', this.systemPreferenceSignal(), ')');
    }

    this.applyTheme(this.resolvedTheme());
  }

  /**
   * Setup listener for system color scheme preference changes
   * If matchMedia is not available, keeps default of 'dark'
   */
  private setupSystemPreferenceListener(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemPreferenceSignal.set(this.mediaQuery.matches ? 'dark' : 'light');
      console.log('[AppearanceService] Detected system preference:', this.mediaQuery.matches ? 'dark' : 'light');

      this.mediaQuery.addEventListener('change', (e) => {
        this.systemPreferenceSignal.set(e.matches ? 'dark' : 'light');
        console.log('[AppearanceService] System preference changed:', e.matches ? 'dark' : 'light');

        // Re-apply theme if in auto mode
        if (this.currentThemeSignal() === 'auto') {
          this.applyTheme(this.resolvedTheme());
        }
      });
    } else {
      // Cannot detect system preference - default remains 'dark'
      console.log('[AppearanceService] Cannot detect system preference - defaulting to dark');
    }
  }

  /**
   * Set the application theme
   * @param theme - The theme to set ('light', 'dark', or 'auto')
   */
  setTheme(theme: AppTheme): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.STORAGE_KEY, theme);
    }
    this.currentThemeSignal.set(theme);
    this.applyTheme(this.resolvedTheme());
    console.log('[AppearanceService] Theme set to:', theme, '(resolved:', this.resolvedTheme(), ')');
  }

  /**
   * Apply the theme to the DOM
   */
  private applyTheme(resolved: ResolvedTheme): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const html = this.doc.documentElement;
    const body = this.doc.body;

    // Remove existing theme classes
    html.classList.remove('theme-light', 'theme-dark');
    body.classList.remove('theme-light', 'theme-dark');

    // Add new theme class
    const themeClass = `theme-${resolved}`;
    html.classList.add(themeClass);
    body.classList.add(themeClass);

    // Set data attribute for CSS targeting
    html.setAttribute('data-theme', resolved);
    body.setAttribute('data-theme', resolved);

    console.log('[AppearanceService] Applied theme class:', themeClass);
  }

  /**
   * Get available theme options for UI
   */
  getThemeOptions(): ThemeOption[] {
    return [
      { value: 'light', labelKey: 'wizard.step1.main.theme.light', icon: 'light_mode' },
      { value: 'dark', labelKey: 'wizard.step1.main.theme.dark', icon: 'dark_mode' },
      { value: 'auto', labelKey: 'wizard.step1.main.theme.auto', icon: 'brightness_auto' }
    ];
  }

  /**
   * Toggle between light and dark themes (skips auto)
   */
  toggleTheme(): void {
    const current = this.resolvedTheme();
    this.setTheme(current === 'light' ? 'dark' : 'light');
  }
}
